import type { CarState, WindowId } from "../types";
import { PATHS } from "./Icons";

// Top-down 2018 Maruti Suzuki Dzire (3rd gen) at true plan proportions: 3995 × 1735 mm,
// 2450 mm wheelbase, drawn at 0.138 px/mm (front bumper y=24, rear y=575, body x=30..270).
// Live state: underglow cyan = locked / amber = unlocked; each door glass retracts as it opens;
// headlamps + tail lamps light with the engine; boot lid lifts; AC vents pulse on the cowl.

export interface Paint { name: string; base: string; shade: string; hi: string; }
// 2018 Dzire factory palette.
export const PAINTS: Paint[] = [
  { name: "Pearl Arctic White", base: "#e6eaef", shade: "#aeb7c4", hi: "#ffffff" },
  { name: "Silky Silver",       base: "#b6bcc5", shade: "#6f7781", hi: "#eef1f5" },
  { name: "Magma Grey",         base: "#596069", shade: "#2c3037", hi: "#8b929c" },
  { name: "Oxford Blue",        base: "#1f3b70", shade: "#0d1c3d", hi: "#3f66a8" },
  { name: "Gallant Red",        base: "#a8161f", shade: "#560a0f", hi: "#e03a44" },
  { name: "Sherwood Brown",     base: "#5c3e2c", shade: "#2f1f18", hi: "#8f634a" },
];

const ROSE = "#fb7185";

export default function CarGraphic({ s, paint = PAINTS[0] }: { s: CarState; paint?: Paint }) {
  const lockColor = s.locked ? "#22d3ee" : "#f59e0b";
  const lit = s.engineRunning || s.ignition;

  // Door glass in plan view: the strip between the belt line (outer) and the roof edge (inner).
  // As the window drops, a dark gap grows from the belt line inward.
  const win = (id: WindowId, side: "l" | "r", d: string, beltX: number, span: number) => {
    const pos = s.windows[id];
    const openFrac = pos / 100;
    const isOpen = pos > 5;
    const gapW = span * openFrac;
    const gapX = side === "l" ? beltX : beltX - gapW;
    return (
      <g key={id}>
        <clipPath id={`clip-${id}`}><path d={d} /></clipPath>
        <path d={d} fill="url(#glassSide)" stroke="#0b1220" strokeWidth={0.8} />
        <rect
          x={gapX} y={0} width={gapW} height={600} clipPath={`url(#clip-${id})`}
          fill={isOpen ? "#2a0d16" : "transparent"}
          style={{ transition: "x .5s cubic-bezier(.4,0,.2,1), width .5s cubic-bezier(.4,0,.2,1)" }}
        />
        <path d={d} fill="none" stroke={isOpen ? ROSE : "#0b1220"} strokeWidth={isOpen ? 1.4 : 0.8}
          style={{ transition: "stroke .3s" }} />
      </g>
    );
  };

  const lampFill = lit ? "url(#lampOn)" : "url(#lampOff)";
  const tailFill = lit ? "#ff3b4a" : "#7a1520";

  return (
    <svg viewBox="0 0 300 600" className="car-svg" role="img" aria-label="Maruti Suzuki Dzire — live status">
      <defs>
        <linearGradient id="paint" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={paint.shade} />
          <stop offset="0.18" stopColor={paint.base} />
          <stop offset="0.5" stopColor={paint.hi} />
          <stop offset="0.82" stopColor={paint.base} />
          <stop offset="1" stopColor={paint.shade} />
        </linearGradient>
        <linearGradient id="paintV" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.28" />
          <stop offset="0.25" stopColor="#ffffff" stopOpacity="0.04" />
          <stop offset="0.7" stopColor="#000000" stopOpacity="0.05" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.28" />
        </linearGradient>
        <linearGradient id="glass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a5f8a" />
          <stop offset="0.45" stopColor="#12233d" />
          <stop offset="1" stopColor="#0a1526" />
        </linearGradient>
        <linearGradient id="glassSide" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#0d1a2e" />
          <stop offset="1" stopColor="#264a73" />
        </linearGradient>
        <linearGradient id="lampOn" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#bfe9ff" />
        </linearGradient>
        <linearGradient id="lampOff" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#d7dde6" />
          <stop offset="1" stopColor="#8e98a6" />
        </linearGradient>
        <linearGradient id="chrome" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.5" stopColor="#9aa4b2" />
          <stop offset="1" stopColor="#e6ebf1" />
        </linearGradient>
        <radialGradient id="core" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#22d3ee" stopOpacity="0.9" />
          <stop offset="1" stopColor="#22d3ee" stopOpacity="0" />
        </radialGradient>
        <filter id="glow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="underglow" x="-30%" y="-15%" width="160%" height="130%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
        <filter id="shadow" x="-20%" y="-10%" width="140%" height="120%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>

      {/* underglow: lock state */}
      <path d={BODY} fill={lockColor} opacity="0.55" filter="url(#underglow)" style={{ transition: "fill .4s" }} />
      {/* ground shadow */}
      <path d={BODY} fill="#000" opacity="0.6" filter="url(#shadow)" transform="translate(0 6)" />

      {/* wheels (165/80 R14) — mostly under the fenders */}
      {[[26, 90], [254, 90], [26, 428], [254, 428]].map(([x, y]) => (
        <rect key={`${x}${y}`} x={x} y={y} width="20" height="88" rx="9" fill="#0a0c10" stroke="#23282f" strokeWidth="1" />
      ))}

      {/* ORVMs (body colour, electric) */}
      <path d="M31 168 L14 160 Q6 166 12 176 L31 178 Z" fill="url(#paint)" stroke={paint.shade} strokeWidth="0.8" />
      <path d="M269 168 L286 160 Q294 166 288 176 L269 178 Z" fill="url(#paint)" stroke={paint.shade} strokeWidth="0.8" />

      {/* body */}
      <path d={BODY} fill="url(#paint)" stroke={paint.shade} strokeWidth="1.2" />
      <path d={BODY} fill="url(#paintV)" />

      {/* bonnet: shut lines + twin character creases */}
      <path d="M68 48 L232 48 L246 150 L54 150 Z" fill="none" stroke={paint.shade} strokeWidth="0.9" opacity="0.7" />
      <path d="M118 56 L108 146 M182 56 L192 146" fill="none" stroke="#ffffff" strokeWidth="0.8" opacity="0.35" />
      <path d="M120 58 L110 148 M184 58 L194 148" fill="none" stroke={paint.shade} strokeWidth="0.8" opacity="0.5" />

      {/* front: hexagonal grille with chrome bar, Suzuki S, headlamps */}
      <path d="M104 30 L196 30 L206 40 L200 50 L100 50 L94 40 Z" fill="#0b0e14" stroke="#1d232c" strokeWidth="1" />
      <path d="M104 30 L196 30 L200 35 L100 35 Z" fill="url(#chrome)" />
      <path d="M96 44 H204" stroke="#161b24" strokeWidth="1.5" />
      <path d="M150 34 l6 3 -6 10 -6 -10 z" fill="url(#chrome)" opacity="0.95" />
      {[
        { d: "M102 31 L60 36 C50 50 48 72 58 92 L80 84 C80 62 90 44 102 31 Z", cx: 74, cy: 58 },
        { d: "M198 31 L240 36 C250 50 252 72 242 92 L220 84 C220 62 210 44 198 31 Z", cx: 226, cy: 58 },
      ].map(({ d, cx, cy }) => (
        <g key={cx} filter={lit ? "url(#glow)" : undefined}>
          <path d={d} fill="#161b23" />
          <path d={d} fill={lampFill} opacity={lit ? 1 : 0.55} transform={`translate(${cx > 150 ? -1.5 : 1.5} 1.5)`} style={{ transition: "opacity .3s" }} />
          <circle cx={cx} cy={cy} r="7" fill={lit ? "#ffffff" : "#0e131a"} stroke="#9aa4b2" strokeWidth="1.2" />
          <circle cx={cx} cy={cy} r="3" fill={lit ? "#dff6ff" : "#3a4552"} />
        </g>
      ))}
      {/* front bumper split line */}
      <path d="M58 44 C90 40 210 40 242 44" fill="none" stroke={paint.shade} strokeWidth="0.8" opacity="0.6" />

      {/* cowl + wipers */}
      <path d="M52 150 Q150 140 248 150" fill="none" stroke="#0b1220" strokeWidth="2" />
      <path d="M78 149 L140 141 M160 149 L222 141" stroke="#111823" strokeWidth="1.6" strokeLinecap="round" />

      {/* windshield */}
      <path d="M48 153 Q150 139 252 153 L232 213 Q150 201 68 213 Z" fill="url(#glass)" stroke="#0b1220" strokeWidth="1" />
      <path d="M62 158 Q150 148 238 158 L226 176 Q150 168 74 176 Z" fill="#ffffff" opacity="0.08" />

      {/* roof */}
      <path d="M68 213 Q150 201 232 213 L232 372 Q150 380 68 372 Z" fill="url(#paint)" stroke={paint.shade} strokeWidth="0.8" />
      <path d="M68 213 Q150 201 232 213 L232 372 Q150 380 68 372 Z" fill="url(#paintV)" />
      <path d="M100 222 L100 366 M200 222 L200 366" stroke="#000" strokeWidth="0.8" opacity="0.12" />
      <path d="M150 361 v-8" stroke="#0b1220" strokeWidth="2" strokeLinecap="round" />

      {/* rear glass + C-pillars */}
      <path d="M70 372 Q150 380 230 372 L244 426 Q150 440 56 426 Z" fill="url(#glass)" stroke="#0b1220" strokeWidth="1" />
      <path d="M96 400 Q150 408 204 400" fill="none" stroke="#ff9a00" strokeWidth="0.6" opacity="0.35" />

      {/* side glass — front doors then rear doors */}
      {win("fl", "l", "M42 160 L68 215 L68 282 L42 282 Z", 42, 26)}
      {win("fr", "r", "M258 160 L232 215 L232 282 L258 282 Z", 258, 26)}
      {win("rl", "l", "M42 288 L68 288 L68 370 L44 392 Z", 42, 26)}
      {win("rr", "r", "M258 288 L232 288 L232 370 L256 392 Z", 258, 26)}

      {/* door shut lines + handles */}
      <path d="M30 152 L42 160 M30 284 L42 284 M30 402 L44 392 M270 152 L258 160 M270 284 L258 284 M270 402 L256 392"
        fill="none" stroke={paint.shade} strokeWidth="1" />
      {[[33, 262], [33, 378], [261, 262], [261, 378]].map(([x, y]) => (
        <rect key={`${x}${y}`} x={x} y={y} width="6" height="14" rx="2" fill="url(#chrome)" opacity="0.9" />
      ))}

      {/* boot lid — lifts when open */}
      <g style={{ transition: "transform .5s cubic-bezier(.4,0,.2,1)" }} transform={s.bootOpen ? "translate(0 -10)" : undefined}>
        <path d="M56 428 Q150 442 244 428 L252 530 Q150 540 48 530 Z"
          fill={s.bootOpen ? "#2a0d16" : "url(#paint)"} stroke={s.bootOpen ? ROSE : paint.shade} strokeWidth={s.bootOpen ? 1.4 : 0.9}
          style={{ transition: "fill .4s, stroke .4s" }} />
        {!s.bootOpen && <path d="M56 428 Q150 442 244 428 L252 530 Q150 540 48 530 Z" fill="url(#paintV)" />}
        {/* chrome garnish + boot release switch */}
        <path d="M70 520 Q150 528 230 520 L230 526 Q150 534 70 526 Z" fill="url(#chrome)" opacity="0.95" />
        <rect x="143" y="521" width="14" height="6" rx="2" fill="#0b0e14" opacity="0.85" />
        <path d="M150 466 l5 3 -5 8 -5 -8 z" fill="url(#chrome)" opacity="0.9" />
      </g>

      {/* wrap-around tail lamps */}
      <path d="M40 500 C34 522 36 550 46 562 L98 561 C94 544 84 522 68 506 Z" fill={tailFill} stroke="#3a0c12" strokeWidth="0.8" filter={lit ? "url(#glow)" : undefined} />
      <path d="M260 500 C266 522 264 550 254 562 L202 561 C206 544 216 522 232 506 Z" fill={tailFill} stroke="#3a0c12" strokeWidth="0.8" filter={lit ? "url(#glow)" : undefined} />
      <path d="M56 540 C58 548 62 555 66 558 M244 540 C242 548 238 555 234 558" fill="none" stroke="#fff" strokeWidth="1" opacity="0.5" />
      {/* rear bumper split line */}
      <path d="M48 556 C90 552 210 552 252 556" fill="none" stroke={paint.shade} strokeWidth="0.8" opacity="0.6" />

      {/* engine core (K12M) */}
      {s.engineRunning && (
        <>
          <circle cx="150" cy="100" r="34" fill="url(#core)" className="pulse" />
          <circle cx="150" cy="100" r="7" fill="#67e8f9" filter="url(#glow)" className="pulse" />
        </>
      )}

      {/* AC — dash vents just behind the cowl */}
      {s.ac && (
        <g className="pulse-soft">
          {[124, 137, 163, 176].map((x) => (
            <rect key={x} x={x - 3} y={160} width="6" height="14" rx="3" fill="#38bdf8" opacity="0.9" filter="url(#glow)" />
          ))}
        </g>
      )}

      {/* lock / kill badge on the roof */}
      <g transform="translate(150 292)">
        <circle r="17" fill="#0a1424" stroke={s.killArmed ? ROSE : lockColor} strokeWidth="1.6" opacity="0.92" />
        <path
          d={PATHS[s.killArmed ? "ban" : s.locked ? "lock" : "unlock"]}
          transform="translate(-9 -9) scale(0.75)"
          fill="none" stroke={s.killArmed ? ROSE : lockColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

// Plan-view body outline: rounded front bumper, fenders swelling to the doors, gentle rear taper.
const BODY =
  "M150 24 C205 24 244 30 254 48 C262 66 266 105 268 150 L270 250 L270 430 " +
  "C269 480 264 530 254 556 C246 572 210 575 150 575 C90 575 54 572 46 556 " +
  "C36 530 31 480 30 430 L30 250 L32 150 C34 105 38 66 46 48 C56 30 95 24 150 24 Z";
